export interface ProjectProperties {
  name: string;
  description: string;
  domainId: string;
  boundedContextId?: string;
  metadata?: Record<string, unknown>;
}

export class Project {
  readonly id: string;
  readonly createdAt: Date;
  private _updatedAt: Date;
  private _name: string;
  private _description: string;
  private _domainId: string;
  private _boundedContextId?: string;
  private _metadata: Record<string, unknown>;

  private constructor(properties: ProjectProperties, id?: string) {
    this.id = id || crypto.randomUUID();
    this.createdAt = new Date();
    this._updatedAt = new Date();
    this._name = properties.name;
    this._description = properties.description;
    this._domainId = properties.domainId;
    this._boundedContextId = properties.boundedContextId;
    this._metadata = properties.metadata || {};

    this.validateInvariants();
  }

  static create(properties: ProjectProperties): Project {
    return new Project(properties);
  }

  static reconstitute(properties: ProjectProperties, id: string, createdAt: Date, updatedAt: Date): Project {
    const project = new Project(properties, id);
    (project as any).createdAt = createdAt;
    project._updatedAt = updatedAt;
    return project;
  }

  get name(): string {
    return this._name;
  }

  get description(): string {
    return this._description;
  }

  get domainId(): string {
    return this._domainId;
  }

  get boundedContextId(): string | undefined {
    return this._boundedContextId;
  }

  get metadata(): Record<string, unknown> {
    return { ...this._metadata };
  }

  get updatedAt(): Date {
    return this._updatedAt;
  }

  updateName(name: string): void {
    if (!name || name.trim().length === 0) {
      throw new Error('Project name cannot be empty');
    }
    this._name = name.trim();
    this.touch();
  }

  updateDescription(description: string): void {
    if (!description || description.trim().length === 0) {
      throw new Error('Project description cannot be empty');
    }
    this._description = description.trim();
    this.touch();
  }

  updateBoundedContext(boundedContextId?: string): void {
    this._boundedContextId = boundedContextId;
    this.touch();
  }

  updateMetadata(metadata: Record<string, unknown>): void {
    this._metadata = { ...metadata };
    this.touch();
  }

  private touch(): void {
    this._updatedAt = new Date();
  }

  private validateInvariants(): void {
    if (!this._name || this._name.trim().length === 0) {
      throw new Error('Project name is required');
    }

    if (this._name.length > 100) {
      throw new Error('Project name must be 100 characters or less');
    }

    if (!this._description || this._description.trim().length === 0) {
      throw new Error('Project description is required');
    }

    if (this._description.length > 500) {
      throw new Error('Project description must be 500 characters or less');
    }

    if (!this._domainId || this._domainId.trim().length === 0) {
      throw new Error('Domain ID is required');
    }
  }

  toJSON() {
    return {
      id: this.id,
      name: this._name,
      description: this._description,
      domainId: this._domainId,
      boundedContextId: this._boundedContextId,
      metadata: this._metadata,
      createdAt: this.createdAt,
      updatedAt: this._updatedAt
    };
  }
}